import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { VirtualizedTable } from '@/_shared/components/VirtualizedTable';
import {
  getSurveyCampaignInvitationByTemplateIdCall,
  getSurveysCampaignsCall,
  getTemplatesCall,
  Steps,
} from '../../state';
import 'react-virtualized/styles.css';

const TABLE_MIN_WIDTH = 800;

/**
 * @param filterDepartments - When set (coming from "Vedi persone" in StepRecapDepartments),
 *   the table shows only employees belonging to those departments.
 *   Undefined when reached via "Scelta individuale" from StepChooser (shows all employees).
 * @param backStep - Step to navigate to when "Indietro" is clicked.
 *   STEP_DEPARTMENTS when reached via "Vedi persone"; STEP_CHOOSER for individual selection.
 *   Going back to STEP_DEPARTMENTS preserves the selection; STEP_CHOOSER also clears it.
 */
type Props = {
  setStep: (step: Steps) => void;
  setSelectedUsersIds: (updater: (prev: string[]) => string[]) => void;
  selectedUsersIds?: string[];
  currentTemplateId?: string;
  currentVersionId?: number;
  currentCampaignId?: string;
  selectedTemplate?: string;
  filterDepartments?: string[];
  backStep?: Steps;
};

export const StepListEmployees: FC<Props> = ({
  setStep,
  setSelectedUsersIds,
  selectedUsersIds,
  currentTemplateId,
  currentVersionId,
  currentCampaignId,
  selectedTemplate,
  filterDepartments,
  backStep,
}) => {
  const t = useTranslations('surveys.sendSurvey.stepListEmployees');
  const tShared = useTranslations();
  const message = tShared('errors.genericApiError');

  const {
    allEmployees,
    execute: executeGetSurveyCampaignInvitationByTemplateId,
    loading: loadingGetSurveyCampaignInvitationByTemplateId,
    success: successGetSurveyCampaignInvitationByTemplateId,
    error: errorGetSurveyCampaignInvitationByTemplateId,
  } = getSurveyCampaignInvitationByTemplateIdCall.hook();

  const { surveyId } = useParams();

  const { response: responseGetSurveysCampaigns } = getSurveysCampaignsCall.hook();
  const { response: responseGetTemplates } = getTemplatesCall.hook();

  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);

  const displayedEmployees =
    filterDepartments && filterDepartments.length > 0
      ? (allEmployees ?? []).filter(
          emp => emp.department && filterDepartments.includes(emp.department),
        )
      : allEmployees;

  const customSelectedUser = useCallback(
    (selectedIds: string[]) => {
      setSelectedUsersIds(() => selectedIds);
    },
    [setSelectedUsersIds],
  );

  const resolvedBackStep = backStep ?? Steps.STEP_CHOOSER;

  const goBack = () => {
    setStep(resolvedBackStep);
    if (resolvedBackStep === Steps.STEP_CHOOSER) {
      setSelectedUsersIds(() => []);
    }
  };

  useEffect(() => {
    const internalSurveyId = selectedTemplate
      ? responseGetTemplates?.find(template => template.name === selectedTemplate)?.id
      : surveyId;
    const versions = responseGetTemplates?.find(
      template => template.id === internalSurveyId,
    )?.versions;
    const lastTemplateVersion = versions ? versions[versions.length - 1] : undefined;

    const internalCampaignId = selectedTemplate
      ? responseGetSurveysCampaigns?.findLast(
          campaign => campaign.surveyTemplateId === internalSurveyId,
        )?.id
      : currentCampaignId;

    if (internalSurveyId && lastTemplateVersion && lastTemplateVersion.version) {
      executeGetSurveyCampaignInvitationByTemplateId({
        campaignId: internalCampaignId || undefined,
        templateId: currentTemplateId || (internalSurveyId as string),
        versionId: currentVersionId?.toString() || lastTemplateVersion.version?.toString(),
      });
    }
  }, [
    currentCampaignId,
    currentTemplateId,
    currentVersionId,
    executeGetSurveyCampaignInvitationByTemplateId,
    responseGetSurveysCampaigns,
    responseGetTemplates,
    selectedTemplate,
    surveyId,
  ]);

  useEffect(() => {
    const widthBox = (ref.current as HTMLDivElement)?.offsetWidth;
    const PADDING = 20;
    setWidth(Math.max(widthBox - PADDING, TABLE_MIN_WIDTH));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box ref={ref} mb='xs' mt='m' width='100%'>
      <Typography textAlign='center' variant='p2/light'>
        {filterDepartments && filterDepartments.length > 0
          ? t('descriptionForEmployeeOfDepartment')
          : t('description')}
      </Typography>
      {loadingGetSurveyCampaignInvitationByTemplateId && (
        <Box mt='l' textAlign='center'>
          <Loader size='s' />
        </Box>
      )}
      {errorGetSurveyCampaignInvitationByTemplateId && (
        <AlertBadge containerProps={{ mt: 'l' }} iconName='info' type='error' label={message} />
      )}
      {successGetSurveyCampaignInvitationByTemplateId && (
        <Box mt='m'>
          <VirtualizedTable
            tableWidth={width}
            showCheckboxes
            checkBoxesCallback={customSelectedUser}
            initialSelectedIds={selectedUsersIds}
            data={displayedEmployees}
            headerLocales='surveys.sendSurvey.stepListEmployees.table'
            isSearchable
            searchLabel={t('searchPlaceholder')}
            columnWidths={{ name: 100, departement: 100, role: 250, email: 250 }}
          />
        </Box>
      )}
      <Box justifyContent='flex-end' display='flex' flexDirection='row'>
        <Button onClick={goBack} mt='l' mr='xs' variant='link/primary'>
          {t('buttons.back')}
        </Button>
        <Button
          disabled={
            !!errorGetSurveyCampaignInvitationByTemplateId ||
            (selectedUsersIds && selectedUsersIds.length === 0)
          }
          onClick={() => setStep(Steps.STEP_RECIPIENTS)}
          mt='l'
          variant='primary'
        >
          {`${t('buttons.continue')}${selectedUsersIds?.length && selectedUsersIds?.length > 0 ? ` (${selectedUsersIds?.length})` : ''}`}
        </Button>
      </Box>
    </Box>
  );
};
